import { Component, EventEmitter, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { NotaFiscalService } from '../../../../services/nota-fiscal-servico.service';
import { JwtHelper } from '../../../../helpers/jwtDecode.helper';
import { SpinnerService } from '../../../../services/spinner.service';
import { ToastService } from '../../../../services/toast.service';
import { SeveridadeToast } from '../../../../models/enums/severidade-toast.enum';
import * as XLSX from 'xlsx';
import { retornaDescricao } from '../../../../shared/mock/descricao';
import { AuthService } from '../../../../services/auth.service';
import { UsuarioAllProperties } from '../../../../models/usuarioAllProperties.model';

@Component({
  selector: 'app-importar-nfse-emitida',
  templateUrl: './importar-nfse-emitida.component.html',
  styleUrls: ['./importar-nfse-emitida.component.scss'],
  providers: [MessageService]
})
export class ImportarNfseEmitidaComponent implements OnInit {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();
  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  private userId: string;
  uploadedFiles: any[] = [];
  dataCriacaoUsuario: Date;
  usuario: UsuarioAllProperties;

  @ViewChild('fileInput') fileInput: ElementRef;

  mostrarPreVisualizacao: boolean = false;
  dadosPlanilha: any[] = [];
  nomeArquivo: string = '';
  tamanhoArquivo: number = 0;
  columns: string[] = [];
  errorMessage: string = '';
  loadingPreview: boolean = false;

  constructor(
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private notaFiscalService: NotaFiscalService,
    private jwtHelper: JwtHelper,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private authService: AuthService
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Importar Nfse Emitida');
    this.iconeChange.emit('pi pi-upload');
    this.tituloDescricao.emit(retornaDescricao('importarNfse').titulo);
    this.textoDescricao.emit(retornaDescricao('importarNfse').texto);
    this.mostrarDescricao.emit(true)
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  mostrarAjuda(): void {
    this.toastService.setMensagemToast(
      'Funcionalidade de ajuda será implementada em breve.',
      SeveridadeToast.Info
    );
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || 
          file.name.endsWith('.xls')) {
        
        if (file.size <= 5000000) {
          this.uploadedFiles = [file];
          this.toastService.setMensagemToast(
            `Arquivo "${file.name}" selecionado com sucesso!`,
            SeveridadeToast.Success
          );
        } else {
          this.toastService.setMensagemToast(
            'O arquivo é muito grande. O tamanho máximo permitido é 5MB.',
            SeveridadeToast.Error
          );
        }
      } else {
        this.toastService.setMensagemToast(
          'Formato de arquivo não suportado. Use apenas arquivos .xlsx ou .xls.',
          SeveridadeToast.Error
        );
      }
    }
  }

  triggerFileSelect(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  preVisualizarPlanilha() {
    if (this.uploadedFiles.length === 0) {
      this.toastService.setMensagemToast('Nenhum arquivo selecionado para pré-visualização.', SeveridadeToast.Error);
      return;
    }

    const file = this.uploadedFiles[0];
    this.loadingPreview = true;
    this.mostrarPreVisualizacao = true;
    this.errorMessage = '';

    try {
      this.nomeArquivo = file.name;
      this.tamanhoArquivo = file.size;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            this.errorMessage = 'A planilha deve conter pelo menos um cabeçalho e uma linha de dados.';
            this.loadingPreview = false;
            return;
          }
          const headers = jsonData[1] as string[];
          this.columns = headers;
          this.dadosPlanilha = this.processarDadosPlanilha(jsonData.slice(2), headers);
          this.loadingPreview = false;
          this.toastService.setMensagemToast('Planilha carregada com sucesso!', SeveridadeToast.Success);
        } catch (error) {
          console.error('Erro ao processar planilha:', error);
          this.errorMessage = 'Erro ao processar a planilha. Verifique se o arquivo é válido.';
          this.loadingPreview = false;
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Erro ao carregar arquivo:', error);
      this.errorMessage = 'Erro ao carregar o arquivo.';
      this.loadingPreview = false;
    }
  }

  private processarDadosPlanilha(data: any[], headers: string[]): any[] {
    const processedData = data.map((row: any, index: number) => {
      const processedRow: any = {};

      headers.forEach((header: string, colIndex: number) => {
        const value = row[colIndex] || '';

        if (header.toLowerCase().includes('data da emissão') || header.toLowerCase().includes('emissão') || header.toLowerCase().includes('emissao')) {
          processedRow.dataEmissao = this.formatarData(value);
        } else if (header.toLowerCase().includes('tipo tomador')) {
          processedRow.tipoTomador = value;
        } else if (header.toLowerCase().includes('cpf tomador')) {
          processedRow.cpfTomador = value;
        } else if (header.toLowerCase().includes('cnpj do tomador')) {
          processedRow.cnpjTomador = value;
        } else if (header.toLowerCase().includes('nome do tomador')) {
          processedRow.nomeTomador = value;
        } else if (header.toLowerCase().includes('numero nfse')) {
          processedRow.numeroNfse = value;
        } else if (header.toLowerCase().includes('código do serviço')) {
          processedRow.codigoServico = value;
        } else if (header.toLowerCase().includes('descrição dos serviços')) {
          processedRow.descricaoServicos = value;
        } else if (header.toLowerCase().includes('valor do serviço')) {
          processedRow.valorServico = value;
        } else {
          processedRow[header] = value;
        }
      });

      return processedRow;
    });

    const filteredData = processedData.filter(row => {
      const hasValidData = row.dataEmissao || row.numeroNfse || row.nomeTomador;
      return hasValidData;
    });

    return filteredData;
  }

  private formatarData(value: any): string {
    
    if (!value) {
      return '';
    }
    
    try {
      if (!isNaN(value)) {
        const excelDate = new Date((value - 25569) * 86400000);
        const utcDate = new Date(excelDate.getTime() + (excelDate.getTimezoneOffset() * 60000));
        
        const dia = String(utcDate.getDate()).padStart(2, '0');
        const mes = String(utcDate.getMonth() + 1).padStart(2, '0');
        const ano = utcDate.getFullYear();
        
        return `${dia}/${mes}/${ano}`;
      }

      if (typeof value === 'string') {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
          return value;
        }
        
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const dia = String(date.getDate()).padStart(2, '0');
          const mes = String(date.getMonth() + 1).padStart(2, '0');
          const ano = date.getFullYear();
          
          return `${dia}/${mes}/${ano}`;
        }
      }

      if (value instanceof Date) {
        const dia = String(value.getDate()).padStart(2, '0');
        const mes = String(value.getMonth() + 1).padStart(2, '0');
        const ano = value.getFullYear();
        
        return `${dia}/${mes}/${ano}`;
      }

      return value.toString();
    } catch (error) {
      console.error('Erro ao formatar data:', error, 'Valor:', value);
      return value.toString();
    }
  }

  formatarTamanhoArquivo(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  ocultarPreVisualizacao() {
    this.mostrarPreVisualizacao = false;
    this.dadosPlanilha = [];
    this.loadingPreview = false;
    this.nomeArquivo = '';
    this.tamanhoArquivo = 0;
    this.columns = [];
    this.errorMessage = '';
  }

  retornaUsuario(userId: string) {
    this.authService.obterUsuarioAllPropertiesPorId(userId).subscribe({
      next: (res) => {
        this.usuario = res;
        this.retornaDataCriacaoUsuario(this.usuario)
      },
      error: (e) => {
        this.toastService.setMensagemToast(`Erro ao obter usuário.`, SeveridadeToast.Error);
      }
    })
  }

  private retornaUsuarioAsync(userId: string): Promise<Date> {
  return new Promise((resolve, reject) => {
    this.authService.obterUsuarioAllPropertiesPorId(userId).subscribe({
      next: (res) => {
        const fullDate = new Date(res.dataVerificacaoEmail);
        const dataCriacao = new Date(fullDate.getFullYear(), fullDate.getMonth(), fullDate.getDate());
        resolve(dataCriacao);
      },
      error: () => {
        this.toastService.setMensagemToast(`Erro ao obter usuário.`, SeveridadeToast.Error);
        reject();
      }
    });
  });
}


  retornaDataCriacaoUsuario(usuario: UsuarioAllProperties) {
    const fullDate = new Date(usuario.dataVerificacaoEmail);
    this.dataCriacaoUsuario = new Date(fullDate.getFullYear(), fullDate.getMonth(), fullDate.getDate());
  }

  private converterDataBrParaDate(dataStr: string): Date {
    const [dia, mes, ano] = dataStr.split('/');
    return new Date(Number(ano), Number(mes) - 1, Number(dia));
  }

  onFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || 
          file.name.endsWith('.xls')) {
        
        if (file.size <= 5000000) {
          this.uploadedFiles = [file];
          this.toastService.setMensagemToast(
            `Arquivo "${file.name}" selecionado com sucesso!`,
            SeveridadeToast.Success
          );
        } else {
          this.toastService.setMensagemToast(
            'O arquivo é muito grande. O tamanho máximo permitido é 5MB.',
            SeveridadeToast.Error
          );
          this.fileInput.nativeElement.value = '';
        }
      } else {
        this.toastService.setMensagemToast(
          'Formato de arquivo não suportado. Use apenas arquivos .xlsx ou .xls.',
          SeveridadeToast.Error
        );
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  removeFile(index: number): void {
    const removedFile = this.uploadedFiles.splice(index, 1);
    this.toastService.setMensagemToast(`Você removeu o arquivo: ${removedFile[0]?.name}`, SeveridadeToast.Warn);
    this.fileInput.nativeElement.value = '';
  }

async uploadArquivo(): Promise<void> {
  if (this.uploadedFiles.length === 0) {
    this.toastService.setMensagemToast('Nenhum arquivo foi selecionado para upload.', SeveridadeToast.Error);
    return;
  }

  const arquivo = this.uploadedFiles[0];
  this.spinnerService.show('Processando planilha...');

  try {
    this.dataCriacaoUsuario = await this.retornaUsuarioAsync(this.userId);

    this.notaFiscalService.lerPlanilha(arquivo).subscribe({
      next: (nfseDtos) => {
        const algumaDataInvalida = nfseDtos.some((dto: any) => {
          if (!dto.dataEmissao) return true;

          const dataEmissao = this.converterDataBrParaDate(dto.dataEmissao);

          const dataEmissaoMes = dataEmissao.getMonth();
          const dataEmissaoAno = dataEmissao.getFullYear();
          const criacaoMes = this.dataCriacaoUsuario.getMonth();
          const criacaoAno = this.dataCriacaoUsuario.getFullYear();

          return (
            dataEmissaoAno < criacaoAno ||
            (dataEmissaoAno === criacaoAno && dataEmissaoMes < criacaoMes)
          );
        });

        if (algumaDataInvalida) {
          this.toastService.setMensagemToast(
            'Não é permitido importar NFS-e com data de emissão anterior ao mês de cadastro do usuário.',
            SeveridadeToast.Error
          );
          this.spinnerService.hide();
          return;
        }

        this.notaFiscalService.listarCodigosEmitidos(this.userId).subscribe({
          next: (codigosExistentes: string[]) => {
            const codigosSet = new Set(codigosExistentes.map(c => c.replace(/^0+/, '')));

            const codigosDuplicados = nfseDtos
            .map((dto: any) => dto.numeroNfse?.toString().replace(/^0+/, '') || '')
            .filter(codigo => codigosSet.has(codigo));

            if (codigosDuplicados.length > 0) {
              const lista = codigosDuplicados.join(', ');
            this.toastService.setMensagemToast(
            `As seguintes NFS-e já existem na base e não podem ser importadas: ${lista}`,
            SeveridadeToast.Error
            );
            this.spinnerService.hide();
            return;
            }

            this.notaFiscalService.processarPlanilha(nfseDtos, this.userId).subscribe({
              next: () => {
                this.toastService.setMensagemToast('Planilha processada com sucesso!', SeveridadeToast.Success);
                this.uploadedFiles = [];
                this.router.navigate(['/listagem-nfs-e']);
              },
              error: (err) => {
                console.error(err);
                this.toastService.setMensagemToast('Erro ao processar a planilha.', SeveridadeToast.Error);
              },
              complete: () => {
                this.spinnerService.hide();
              }
            });
          },
          error: () => {
            this.toastService.setMensagemToast(
              'Erro ao verificar NFS-e já existentes na base.',
              SeveridadeToast.Error
            );
            this.spinnerService.hide();
          }
        });
      },
      error: (err) => {
        console.error(err);
        this.toastService.setMensagemToast('Falha ao ler a planilha.', SeveridadeToast.Error);
        this.spinnerService.hide();
      }
    });
    } catch {
    this.spinnerService.hide();
    }
  }


  formatFileSize(size: number): string {
    if (size === 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  baixarPlanilhaModelo(): void {
    this.spinnerService.show('Baixando planilha modelo...');

    this.notaFiscalService.baixarPlanilhaModelo().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'Planilha_Modelo_NFSe_emitida.xlsx';
        anchor.click();
        window.URL.revokeObjectURL(url);
        this.toastService.setMensagemToast('Download realizado com sucesso.', SeveridadeToast.Success);
      },
      error: (err) => {
        this.toastService.setMensagemToast('Falha ao realizar o download da planilha.', SeveridadeToast.Error);
        console.error(err);
      },
      complete: () => {
        this.spinnerService.hide();
      },
    });
  }

  formatarDataExcelParaISO(data: any): string {
    if (!data) return '';
    
    try {
      if (!isNaN(data)) {
        const excelDate = new Date((data - 25569) * 86400000);
        const utcDate = new Date(excelDate.getTime() + (excelDate.getTimezoneOffset() * 60000));
        
        return utcDate.toISOString();
      }
      
      const parsedDate = new Date(data);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString();
      }

      console.error(`Erro ao converter data: ${data}`);
      return '';
    } catch (error) {
      console.error(`Erro ao converter data: ${data}`, error);
      return '';
    }
  }
}
